import { useMemo, useState } from "react";

import Input from "../../controls/Input";
import { config } from "../../../constants";
import { resolveTenantAssetUrl } from "../../../shared/tenantBranding";
import { MODULE_OPTIONS } from "../../../shared/tenantModules";
import { LABEL_OPTIONS } from "../../../shared/tenantSettingsForm";
import {
  isValidHexColor,
  isValidSlug,
  validateLogoFile,
} from "../../../shared/tenantValidation";
import {
  isDeveloperModeEnabled,
  setDeveloperModeEnabled,
} from "../../../shared/developerMode";

const primaryButtonClass =
  "inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-[#16A34A] bg-[#16A34A] px-4 text-sm font-semibold text-white shadow-sm transition hover:border-[#15803D] hover:bg-[#15803D] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#16A34A] disabled:opacity-50";

const secondaryButtonClass =
  "inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-[#E2E8F0] bg-white px-4 text-sm font-semibold text-[#0F172A] shadow-sm transition hover:bg-[#F8FAFC] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#94A3B8] disabled:opacity-50";

const fullWidthInputClass =
  "block w-full max-w-full rounded-xl border border-[#E2E8F0] bg-white px-3 py-2 text-sm text-[#0F172A] shadow-sm transition placeholder:text-[#64748B]/70 focus:border-[#2563EB] focus:outline-none focus:ring-2 focus:ring-[#2563EB]/10";

function SettingsCard({ title, description, children }) {
  return (
    <section className="overflow-hidden rounded-[18px] border border-[#E2E8F0] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="border-b border-[#E2E8F0] px-6 py-5">
        <h2 className="text-base font-semibold text-[#0F172A]">{title}</h2>
        {description ? (
          <p className="mt-1 text-sm text-[#64748B]">{description}</p>
        ) : null}
      </div>
      <div className="space-y-5 p-6">{children}</div>
    </section>
  );
}

function ColorField({ label, name, value, onChange, error, required = false }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-[#0F172A]">
        {label}
        {required ? <span className="text-[#DC2626]"> *</span> : null}
      </label>
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="color"
          name={`${name}_picker`}
          value={value || "#16A34A"}
          onChange={(e) => onChange(name, e.target.value)}
          className="h-10 w-14 cursor-pointer rounded-lg border border-[#E2E8F0] bg-white p-1"
        />
        <Input
          name={name}
          onChange={(e) => onChange(name, e.target.value)}
          value={value}
          labelOn={false}
          type="text"
          className="block w-full max-w-xs rounded-xl border border-[#E2E8F0] bg-white px-3 py-2 text-sm text-[#0F172A] shadow-sm focus:border-[#16A34A] focus:outline-none focus:ring-2 focus:ring-[#16A34A]/10"
          error={error}
        />
      </div>
    </div>
  );
}

export default function CompanySettingsForm({
  formValues,
  logoPreview,
  logoFile,
  saving,
  uploadingLogo,
  updateField,
  updateModule,
  updateLabel,
  resetChanges,
  handleLogoSelect,
  uploadLogo,
  saveSettings,
}) {
  const [errors, setErrors] = useState({});
  const [developerMode, setDeveloperMode] = useState(isDeveloperModeEnabled);

  const handleDeveloperModeChange = (event) => {
    const enabled = event.target.checked;
    setDeveloperMode(enabled);
    setDeveloperModeEnabled(enabled);
  };

  const displayedLogo = useMemo(() => {
    if (logoPreview) {
      return logoPreview;
    }
    return resolveTenantAssetUrl(formValues.logo_url, config.url.STORAGE_URL);
  }, [formValues.logo_url, logoPreview]);

  const validate = () => {
    const nextErrors = {};

    if (!formValues.name?.trim()) {
      nextErrors.name = "Company name is required.";
    } else if (formValues.name.length > 255) {
      nextErrors.name = "Company name must be 255 characters or fewer.";
    }

    if (!formValues.slug?.trim()) {
      nextErrors.slug = "Company slug is required.";
    } else if (!isValidSlug(formValues.slug)) {
      nextErrors.slug =
        "Slug must be lowercase letters, numbers, and hyphens only.";
    }

    if (!isValidHexColor(formValues.primary_color)) {
      nextErrors.primary_color = "Enter a valid hex color (e.g. #16A34A).";
    }

    if (formValues.sidebarColor && !isValidHexColor(formValues.sidebarColor)) {
      nextErrors.sidebarColor = "Enter a valid hex color.";
    }

    if (formValues.headerColor && !isValidHexColor(formValues.headerColor)) {
      nextErrors.headerColor = "Enter a valid hex color.";
    }

    if (logoFile) {
      const logoError = validateLogoFile(logoFile);
      if (logoError) {
        nextErrors.logo = logoError;
      }
    }

    LABEL_OPTIONS.forEach(({ key }) => {
      const value = formValues.labels?.[key];
      if (value && value.length > 100) {
        nextErrors[`label_${key}`] = "Label must be 100 characters or fewer.";
      }
    });

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      saveSettings(formValues);
    }
  };

  const handleSlugChange = (e) => {
    updateField("slug", e.target.value.toLowerCase());
  };

  const handleLogoInput = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    const logoError = validateLogoFile(file);
    if (logoError) {
      setErrors((current) => ({ ...current, logo: logoError }));
      return;
    }

    setErrors((current) => ({ ...current, logo: null }));
    handleLogoSelect(file);
    uploadLogo(file);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <SettingsCard
        title="Company Profile"
        description="Basic company information shown across the platform."
      >
        <Input
          label="Company Name"
          name="name"
          onChange={(e) => updateField("name", e.target.value)}
          value={formValues.name}
          labelOn={true}
          type="text"
          error={errors.name}
        />
        {/* <Input
          label="Company Slug"
          name="slug"
          onChange={handleSlugChange}
          value={formValues.slug}
          labelOn={true}
          type="text"
          error={errors.slug}
        /> */}
        <Input
          label="Company Email"
          name="company_email"
          onChange={(e) => updateField("company_email", e.target.value)}
          value={formValues.company_email}
          labelOn={true}
          type="email"
        />
        <Input
          label="Phone Number"
          name="phone_number"
          onChange={(e) => updateField("phone_number", e.target.value)}
          value={formValues.phone_number}
          labelOn={true}
          type="text"
        />
        <div className="border-t border-[#E2E8F0] pt-5">
          <div className="mb-4">
            <p className="text-sm font-semibold text-[#0F172A]">Address</p>
            <p className="mt-0.5 text-xs text-[#64748B]">
              Company mailing address for documents and communications.
            </p>
          </div>
          <div className="space-y-4">
            <Input
              label="Address Line 1"
              name="address1"
              onChange={(e) => updateField("address1", e.target.value)}
              value={formValues.address1}
              labelOn={true}
              type="text"
              placeholder="Street address, P.O. box"
              className={fullWidthInputClass}
            />
            <Input
              label="Address Line 2"
              name="address2"
              onChange={(e) => updateField("address2", e.target.value)}
              value={formValues.address2}
              labelOn={true}
              type="text"
              placeholder="Apartment, suite, unit (optional)"
              className={fullWidthInputClass}
            />
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Input
                label="City"
                name="city"
                onChange={(e) => updateField("city", e.target.value)}
                value={formValues.city}
                labelOn={true}
                type="text"
                className={fullWidthInputClass}
              />
              <Input
                label="State / Province"
                name="state"
                onChange={(e) => updateField("state", e.target.value)}
                value={formValues.state}
                labelOn={true}
                type="text"
                className={fullWidthInputClass}
              />
              <Input
                label="Postal Code"
                name="postal_code"
                onChange={(e) => updateField("postal_code", e.target.value)}
                value={formValues.postal_code}
                labelOn={true}
                type="text"
                className={fullWidthInputClass}
              />
            </div>
            <Input
              label="Country"
              name="country"
              onChange={(e) => updateField("country", e.target.value)}
              value={formValues.country}
              labelOn={true}
              type="text"
              className={fullWidthInputClass}
            />
          </div>
        </div>
      </SettingsCard>

      <SettingsCard title="Branding" description="Add your logo.">
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_280px]">
          <div className="space-y-5">
            <div>
              <label className="mb-2 block text-sm font-medium text-[#0F172A]">
                Logo
              </label>
              <input
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                onChange={handleLogoInput}
                disabled={uploadingLogo}
                className="block w-full text-sm text-[#64748B] file:mr-4 file:rounded-lg file:border-0 file:bg-[#F1F5F9] file:px-4 file:py-2 file:text-sm file:font-semibold file:text-[#0F172A] hover:file:bg-[#E2E8F0] disabled:opacity-50"
              />
              {errors.logo ? (
                <p className="mt-1 text-xs font-medium text-[#DC2626]">
                  {errors.logo}
                </p>
              ) : (
                <p className="mt-1 text-xs text-[#64748B]">
                  PNG, JPG, SVG, or WebP. Max 2MB.
                </p>
              )}
              {uploadingLogo ? (
                <p className="mt-3 text-xs font-medium text-[#0F172A]">
                  Uploading logo...
                </p>
              ) : null}
            </div>

            {/* <ColorField
              label="Primary Color"
              name="primary_color"
              value={formValues.primary_color}
              onChange={updateField}
              error={errors.primary_color}
              required
            />
            <ColorField
              label="Sidebar Color"
              name="sidebarColor"
              value={formValues.sidebarColor}
              onChange={updateField}
              error={errors.sidebarColor}
            />
            <ColorField
              label="Header Color"
              name="headerColor"
              value={formValues.headerColor}
              onChange={updateField}
              error={errors.headerColor}
            /> */}
          </div>

          <div className="border-t border-[#E2E8F0] pt-6 text-center lg:min-h-full lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#64748B]">
              Brand Preview
            </p>
            <p className="mt-3 text-sm font-semibold text-[#0F172A]">
              {formValues.name || "Company Name"}
            </p>
            <div className="mt-5 flex min-h-[140px] items-center justify-center rounded-2xl border border-dashed border-[#CBD5E1] bg-[#F8FAFC] px-5 py-6">
              {displayedLogo ? (
                <img
                  src={displayedLogo}
                  alt="Company logo preview"
                  className="max-h-24 w-auto max-w-full object-contain drop-shadow-sm"
                />
              ) : (
                <span className="text-sm text-[#64748B]">No logo selected</span>
              )}
            </div>
            {uploadingLogo ? (
              <p className="mt-3 text-xs font-medium text-[#0F172A]">
                Uploading new logo...
              </p>
            ) : null}
            {/* <button
              type="button"
              className="rounded-lg px-4 py-2 text-sm font-semibold text-white"
              style={{ backgroundColor: formValues.primary_color }}
            >
              Primary Button Preview
            </button>
            <div
              className="rounded-lg px-4 py-3 text-sm font-medium text-white"
              style={{ backgroundColor: formValues.sidebarColor }}
            >
              Sidebar Preview
            </div> */}
          </div>
        </div>
      </SettingsCard>

      {/* <SettingsCard
        title="Enabled Modules"
        description="Choose which modules are available to your team."
      >
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {MODULE_OPTIONS.map(({ key, label }) => (
            <label
              key={key}
              className="flex items-center gap-3 rounded-xl border border-[#E2E8F0] px-4 py-3 text-sm text-[#0F172A]"
            >
              <input
                type="checkbox"
                checked={formValues.modules?.[key] !== false}
                onChange={(e) => updateModule(key, e.target.checked)}
                className="h-4 w-4 rounded border-[#CBD5E1] text-[#16A34A] focus:ring-[#16A34A]"
              />
              <span>{label}</span>
            </label>
          ))}
        </div>
      </SettingsCard>

      <SettingsCard
        title="Custom Labels"
        description="Rename menu items and page titles for your organization."
      >
        <div className="space-y-4">
          {LABEL_OPTIONS.map(({ key, fallback }) => (
            <div
              key={key}
              className="grid gap-3 border-b border-[#F1F5F9] pb-4 last:border-b-0 last:pb-0 sm:grid-cols-[220px_minmax(0,1fr)] sm:items-center"
            >
              <p className="text-sm font-medium text-[#0F172A]">{fallback}</p>
              <Input
                name={`label_${key}`}
                onChange={(e) => updateLabel(key, e.target.value)}
                value={formValues.labels?.[key] ?? ""}
                labelOn={false}
                type="text"
                placeholder={fallback}
                error={errors[`label_${key}`]}
              />
            </div>
          ))}
        </div>
      </SettingsCard> */}

      <SettingsCard
        title="Developer"
        description="Show extra technical details that are hidden in normal use."
      >
        <label className="flex items-start gap-3 rounded-xl border border-[#E2E8F0] px-4 py-3 text-sm text-[#0F172A]">
          <input
            type="checkbox"
            name="developer_mode"
            checked={developerMode}
            onChange={handleDeveloperModeChange}
            className="mt-0.5 h-4 w-4 rounded border-[#CBD5E1] text-[#16A34A] focus:ring-[#16A34A]"
          />
          <span>
            <span className="font-medium">Developer mode</span>
            <span className="mt-0.5 block text-xs text-[#64748B]">
              Show a scannable QR code on a single carton in raw material
              inventory, manufactured inventory, and packaging reports.
              Expanded receiving-invoice carton rows also show a QR code. This
              stays on this browser and is not saved with company settings.
            </span>
          </span>
        </label>
      </SettingsCard>

      <div className="flex flex-wrap justify-end gap-3 rounded-[18px] border border-[#E2E8F0] bg-white px-6 py-5 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
        <button
          type="button"
          className={secondaryButtonClass}
          onClick={resetChanges}
          disabled={saving || uploadingLogo}
        >
          Reset Changes
        </button>
        <button
          type="submit"
          className={primaryButtonClass}
          disabled={saving || uploadingLogo}
        >
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </form>
  );
}
